
import {
  Component,
  Provide,
  Prop,
  Mixins,
  Watch
} from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import {
  IReportRecordsProviderData,
  IReportRecordsProviderMethods
} from "@/models/providers/reports";
import { IReport } from "@/models/reports";

@Component
export default class ReportRecordsProvider extends Mixins(RequestPromises) {
  // Props

  @Prop({ required: true })
  public code!: IReport["report_code"];

  @Prop({ required: true })
  public filters!: any;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  // Data

  public records: any[] = [];
  public total = 0;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      const { data, total } = await this.listRecords();
      this.records = data.result || [];
      this.total = total;
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  // Methods

  public listRecords() {
    return this.$store.dispatch(`data/${DataModules.REPORTS}/listRecords`, {
      reportCode: this.code,
      requestConfig: {
        data: this.filters
      },
      storeData: false,
      vm: this
    });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  // Computed

  public get showCreateReportButton() {
    return !_.isEmpty(_.omit(this.filters, ["limit", "offset"]));
  }

  // Watchers

  @Watch("filters", { deep: true })
  public refresh() {
    this.isLoading = true;
    this.loadData();
  }

  // Provide

  @Provide("reportRecordsProviderData")
  public providerData(): IReportRecordsProviderData {
    return {
      isLoading: this.isLoading,
      records: this.records,
      showCreateReportButton: this.showCreateReportButton,
      total: this.total
    };
  }

  @Provide("reportRecordsProviderMethods")
  public providerMethods: IReportRecordsProviderMethods = {
    refresh: this.refresh
  };

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $reportRecordsData: this.providerData(),
        $reportRecordsMethods: this.providerMethods
      })
    );
  }
}
